/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useContext } from 'react';
import { shape, func, string } from 'prop-types';
import { useQuery } from '@apollo/react-hooks';
import { useDebouncedValueWithCustomDelays } from 'utils/useDebouncedValue';
import { getSkip } from 'components/utils/search';
import ApplicationContext from 'components/Application/Context';
import { trackFilterList } from 'components/PostHog/filterListTracks';
import { useLocation } from 'react-router-dom';
import { getChangedFields, transformPathToName } from './utils';

const defaultSearchResponse = { data: [], meta: {}, facets: {} };
const normalizeResponse = (response) => response || defaultSearchResponse;
// Delay before allowing URL updates after syncing from URL-driven state changes.
// Must be long enough for React to complete the form reset cycle.
const URL_SYNC_DEBOUNCE_MS = 50;
// eslint-disable-next-line max-statements
function FormFilterQuery({
  defaultValues,
  initialValues,
  query,
  render,
  resetForm,
  setFieldValue,
  setValues,
  updateUrl,
  values,
  search,
  transform,
  transformUrl,
  transformVariables,
  variables: initialVariables,
  queryParams,
}) {
  const location = useLocation();
  const [skip, setSkip] = useState(0);
  const [fetchMoreError, setFetchMoreError] = useState(null);
  const initialValuesRef = useRef(initialValues);
  const [prevValues, setPrevValues] = useState(
    initialValues ? initialValues.f : {},
  );
  // Track when we're syncing from URL to prevent race conditions
  // When true, skip URL updates to avoid overwriting during URL-driven resets
  const isSyncingFromUrlRef = useRef(false);

  const debouncedValue = useDebouncedValueWithCustomDelays(
    values,
    300,
    initialValues,
    {
      'f.query': 1000,
    },
  );
  const { user } = useContext(ApplicationContext);
  const page = transformPathToName(location.pathname);

  useEffect(() => {
    const newFilterValues = values.f;
    if (
      newFilterValues &&
      JSON.stringify(prevValues) !== JSON.stringify(newFilterValues)
    ) {
      const filterChanges = getChangedFields(prevValues, newFilterValues);
      setPrevValues(newFilterValues);
      trackFilterList({
        user,
        page,
        filterName: filterChanges.filterName,
        filterValue: filterChanges.filterValue,
      });
    }
  }, [values.f]);

  const transformedValue = transform
    ? transform(debouncedValue)
    : debouncedValue;

  // To ensure backwards compatibility, this new method acts on
  // transformedValue rather than directly on debouncedValue.
  const transformedUrlValue = transformUrl
    ? transformUrl(debouncedValue, transformedValue)
    : transformedValue;

  const queryOptions = React.useMemo(
    () => ({
      variables: transformVariables({
        ...initialVariables,
        ...transformedValue,
      }),
      notifyOnNetworkStatusChange: true,
      ...(queryParams ?? {}),
    }),
    [initialVariables, transformedValue, queryParams],
  );

  const {
    data,
    error,
    fetchMore,
    loading,
    networkStatus,
    variables,
    refetch,
  } = useQuery(query, queryOptions);
  const [entity] = Object.values(data || {});
  const response = normalizeResponse(entity);

  // Add contentIQAverages to response if it exists in the GraphQL data
  if (data?.contentIQAverages) {
    response.contentIQAverages = data.contentIQAverages;
  }

  useEffect(() => {
    // Mark that we're syncing from URL to prevent race condition
    // This prevents the debouncedValue effect from updating URL during reset
    isSyncingFromUrlRef.current = true;
    initialValuesRef.current = initialValues;
    resetForm({ values: initialValues });

    // Clear the sync flag after a short delay to allow the reset to complete
    // and prevent immediate URL updates from stale debounced values
    const timeoutId = setTimeout(() => {
      isSyncingFromUrlRef.current = false;
    }, URL_SYNC_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [initialValues]);

  useEffect(() => {
    const changed = initialValuesRef.current !== values;
    if (changed && values.limit !== defaultValues.limit) {
      // if initiallly the url had a limit, we have to reset the limit
      // to the default after the first mount.
      setFieldValue('limit', defaultValues.limit);
    }

    if (
      values !== debouncedValue &&
      values.skip > 0 &&
      values.skip === debouncedValue.skip
    ) {
      setFieldValue('skip', 0);
    }
  }, [values]);

  useEffect(() => {
    // Skip URL update if we're currently syncing from a URL change
    // This prevents race conditions where stale debounced values overwrite URL state
    if (isSyncingFromUrlRef.current) {
      return;
    }
    updateUrl(transformedUrlValue);
    setSkip(0);
  }, [debouncedValue]);

  async function onLoadMore(config) {
    if (error || fetchMoreError) {
      return;
    }

    const { meta } = entity;
    const limit = skip === 0 ? debouncedValue.limit : defaultValues.limit;
    const nskip = getSkip(meta.total, limit, skip);

    updateUrl(
      { ...transformedUrlValue, limit: nskip + defaultValues.limit },
      true,
    );
    setSkip(nskip);

    try {
      // needs to await to catch errors
      await fetchMore({
        ...config,
        variables: { skip: nskip, limit: defaultValues.limit },
      });
      // eslint-disable-next-line no-empty
    } catch (e) {
      if (!e.message.includes("ObservableQuery with this id doesn't exist:")) {
        setFetchMoreError(e);
        throw e;
      }
    }
  }

  return render({
    error: error ?? fetchMoreError,
    fetchMore: onLoadMore,
    loading,
    setFieldValue,
    setValues,
    refetching: networkStatus === 2,
    reset: () => resetForm({ values: defaultValues }),
    response,
    rootResponse: data,
    variables,
    values,
    search,
    refetch,
    uptodate: debouncedValue === values,
  });
}

FormFilterQuery.propTypes = {
  query: shape({}).isRequired,
  render: func.isRequired,
  setFieldValue: func.isRequired,
  setValues: func.isRequired,
  values: shape({}),
  search: string,
  transformVariables: func,
};

FormFilterQuery.defaultProps = {
  values: {},
  search: '',
  transformVariables: (variables) => variables,
};

export default FormFilterQuery;
